<template>
  <div>
    <div class="row">
      <div class="col-xs-12">
        <div class="ibox">
          <div class="ibox-title m-b-none">
            <div class="flex justify-space-between align-items-baseline">
              <div class="flex">
                <h1>Print jobs</h1>
                <spinner v-if="loading.print_jobs"></spinner>
              </div>
              <div>
                <a @click.prevent="loadPrintJobs"><i class="fa-solid fa-arrows-rotate"></i> Refresh</a>
              </div>
            </div>
          </div>
          <div class="ibox-content p-n">
            <div v-if="!loading.print_jobs" class="print-job-grid grid grid-cols-3 sm-grid-cols-1 md-grid-cols-2 border">
              <div v-for="job in this.printJobs" :key="job.id" class="flex justify-space-between align-items-baseline">
                <div>
                  <h3 class="m-b-none">
                    Print job {{ job.id }}
                  </h3>
                  <p class="text-gray-600 text-12 m-b-none">{{ job.name }}</p>
                  <p v-if="jobSummary(job)" class="text-gray-600 text-12 m-b-none">{{ jobSummary(job) }}</p>
                </div>
                <div class="flex align-items-baseline">
                  <p class="upper-label">
                    {{ job.status }}
                  </p>
                  <a :href="job.doc_to_print.url" v-if="job.status === 'ready' && job.doc_to_print" class="m-l-sm"><i class="fa-solid fa-download"></i></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <div class="ibox">
          <div class="ibox-title">
            <h1>Print an entire roster</h1>
          </div>
          <div class="ibox-content">
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div class="form-group">
                  <label for="roster_select">Roster</label>
                  <spinner v-if="loading.rosters"></spinner>
                  <select v-else id="roster_select" v-model="selectedRosterId" class="form-control" data-cy="roster-print-select">
                    <option :value="null">Choose a roster...</option>
                    <option v-for="roster in rosters" :key="roster.id" :value="roster.id">
                      {{ roster.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-sm-12 col-md-6" v-if="selectedRosterId">
                <spinner v-if="loading.roster_wrestlers"></spinner>
                <div v-else>
                  <p v-if="rosterWrestlers.length === 0">
                    There are no wrestlers on this roster.
                  </p>
                  <template v-else>
                    <p class="m-b-xs">
                      <strong>{{ rosterWrestlers.length }}</strong> {{ rosterWrestlers.length === 1 ? 'wrestler' : 'wrestlers' }} on this roster, {{ cardsPerPageSummary }}.
                    </p>
                    <p v-if="rosterWrestlersMissingCard.length > 0" class="text-gray-600 m-b-none">
                      <strong>{{ rosterWrestlersMissingCard.length }}</strong> {{ rosterWrestlersMissingCard.length === 1 ? 'does' : 'do' }} not have a WIQ ID card yet.
                      A card will be created for them as part of this print job and emailed to the wrestler and guardians on file, exactly like the "Generate WIQ ID Card" button on a wrestler's profile.
                    </p>
                    <p v-else class="text-gray-600 m-b-none">
                      Every wrestler already has a WIQ ID card.
                    </p>
                  </template>
                </div>
              </div>
            </div>
            <div class="row m-t-lg">
              <div class="col-xs-12 text-right">
                <ladda-button @lbClicked="createRosterPrintJob" el-class="btn-primary" :loading="loading.creating_roster" :disabled="!canCreateRosterJob" cypress-class="roster-print-submit">
                  Generate PDF for entire roster
                </ladda-button>
                <div>
                  <small v-show="rosterError"><span class="text-danger">{{ rosterErrorMessage }}</span></small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <div class="ibox">
          <div class="ibox-title flex justify-space-between align-items-baseline">
            <h1>Build a print job</h1>
            <a @click.prevent="clearWrestlers"><i class="fa-solid fa-eraser"></i> Clear selection</a>
          </div>
          <div class="ibox-content">
            <div v-if="loading.wrestlers">
              <spinner></spinner>
            </div>
            <div v-if="!loading.wrestlers">
              <div class="row m-t-m">
                <div class="col-sm-12 col-md-6">
                  <div class="form-group">
                    <label>Wrestlers</label>
                    <div class="" style="position:relative;">
                      <input v-model="search" type="text" id="wrestler_search" placeholder="Find by name or weight.."
                             class="form-control" maxlength="25"/>
                      <i @click="search = ''" v-if="search && search.length > 0"
                         class="cursor-pointer fa-solid fa-xmark color-gray-400"
                         style="position: absolute; top:0; bottom:0; right: 0; transform: translate(-66%, 29%)"></i>
                    </div>
                  </div>
                </div>
                <div class="col-sm-12 col-md-6" v-if="showFilters">
                  <p style="margin-bottom: 5px; font-weight:bold">Filters</p>
                  <div class="form-group">
                    <label class="checkbox-inline">
                      <input type="checkbox" id="gues_checkbox" v-model="showGuests"> Show guest wrestlers
                    </label>
                  </div>
                </div>
                <div class="col-sm-12 m-t-m">
                  <div>
                    <p v-if="wrestlersRemaining > 0">
                      Select up to {{ wrestlersRemaining }} more wrestlers
                    </p>
                    <p v-else-if="wrestlersRemaining === 0">
                      You're all set, click generate to generate the PDF for these 10 wrestlers.
                    </p>
                    <p v-else>
                      You've selected too many wrestlers. Please only select up to 10 wrestlers.
                    </p>
                  </div>
                  <div v-for="wp in searchedProfiles"
                       class="profile-wrapper col-sm-4">
                    <div class="d-inline-block">
                      <input
                          type="checkbox"
                          v-model="wp.checked"
                      />
                    </div>
                    <div class="d-inline-block m-l-xs">
                      <div class='search-result'>
                        <div class='hr-line-dashed'></div>
                        <h3>
                          {{ wp.full_name }}
                          <small class="roster-weight">
                            {{ wp.weight_class }}
                          </small>
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row m-t-lg">
                <div class="col-xs-12 text-right">
                  <ladda-button @lbClicked="createPrintJob" el-class="btn-primary" :loading="loading.creating" :disabled="selectedWrestlers.length > 10">
                    Generate
                  </ladda-button>
                  <div>
                    <small v-show="tooManyWrestlers"><span class="text-danger">Too many wrestlers selected. Max of 10 id cards per print job.</span></small>
                    <small v-show="error"><span class="text-danger">{{ errorMessage }}</span></small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { errorableMix } from './mixins/errorable_mix';
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';

export default {
  name: 'print-wiq-ids',
  components: {
    LaddaButton,
    Spinner,
  },
  mixins: [errorableMix],
  props: {
    showFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      wrestlers: [],
      printJobs: [],
      rosters: [],
      rosterWrestlers: [],
      selectedRosterId: null,
      rosterError: false,
      rosterErrorMessage: '',
      search: '',
      loading: {
        wrestlers: false,
        print_jobs: false,
        creating: false,
        rosters: false,
        roster_wrestlers: false,
        creating_roster: false,
      },
      showGuests: false,
    };
  },
  watch: {
    // whenever showGuests changes, this function will run
    showGuests() {
      this.loadProfiles();
    },
    selectedRosterId(rosterId) {
      this.rosterError = false;
      this.rosterWrestlers = [];
      // Any in flight request belongs to the previous selection and is ignored when it lands
      this.loading.roster_wrestlers = false;
      if (rosterId) {
        this.loadRosterWrestlers(rosterId);
      }
    },
  },
  computed: {
    wrestlersRemaining() {
      return 10 - this.selectedWrestlers.length;
    },
    tooManyWrestlers() {
      return this.selectedWrestlers.length > 10;
    },
    searchedProfiles() {
      if (!this.search || this.search === '') {
        return this.wrestlers;
      }

      if (isNaN(this.search)) {
        return this.wrestlers.filter((wp) => wp.full_name.toLowerCase()
          .includes(this.search));
      }
      return this.wrestlers.filter((wp) => wp.weight_class.includes(this.search));
    },
    selectedWrestlers() {
      return this.wrestlers.filter((wp) => wp.checked);
    },
    printJobParams() {
      return {
        name: 'WIQ Batch Print Job',
        version: 'v1',
        type: 'WiqIdPrintJob',
        args: {
          wrestler_profile_ids: _.map(this.selectedWrestlers, 'id'),
        },
      };
    },
    selectedRoster() {
      return this.rosters.find((r) => r.id === this.selectedRosterId);
    },
    rosterWrestlersMissingCard() {
      return this.rosterWrestlers.filter((wp) => !wp.wiq_athlete_id_url);
    },
    cardsPerPageSummary() {
      const pages = Math.ceil(this.rosterWrestlers.length / 10);
      return `${pages} ${pages === 1 ? 'page' : 'pages'} of 10 cards`;
    },
    canCreateRosterJob() {
      return !!this.selectedRosterId && !this.loading.roster_wrestlers && this.rosterWrestlers.length > 0;
    },
    rosterPrintJobParams() {
      const rosterName = this.selectedRoster ? this.selectedRoster.name : 'Roster';
      return {
        name: `WIQ Roster Print Job - ${rosterName}`,
        version: 'v1',
        type: 'WiqIdPrintJob',
        args: {
          roster_id: this.selectedRosterId,
        },
      };
    },
  },
  created() {
    this.loadProfiles();
    this.loadPrintJobs();
    this.loadRosters();
  },
  methods: {
    // Roster jobs record what ended up in the PDF (see WiqIdPrintJob#generate_print_job!)
    jobSummary(job) {
      const { result } = job;
      if (!result || Array.isArray(result)) {
        return null;
      }
      if (job.status === 'failed' && result.error) {
        return result.error;
      }
      if (!result.cards) {
        return null;
      }
      const parts = [`${result.cards} ${result.cards === 1 ? 'card' : 'cards'}`, `${result.pages} ${result.pages === 1 ? 'page' : 'pages'}`];
      if (result.generated_cards > 0) {
        parts.push(`${result.generated_cards} newly generated`);
      }
      if (result.skipped && result.skipped.length > 0) {
        parts.push(`${result.skipped.length} skipped`);
      }
      return parts.join(', ');
    },
    loadRosters() {
      this.loading.rosters = true;
      const url = `${this.$apiService.rostersUrl()}?per_page=100`;
      this.$apiService.loadAllPages(url, 'rosters')
        .then((rosters) => {
          this.rosters = _.sortBy(rosters, 'name');
          this.loading.rosters = false;
        })
        .catch((error) => {
          this.loading.rosters = false;
          this.rosterErrorMessage = `Error retrieving rosters ${error.toString()}`;
          this.rosterError = true;
        });
    },
    // Mirrors the server side scope for roster jobs: teammates and guests, no alumni
    loadRosterWrestlers(rosterId) {
      const url = this.$apiService.filteredWrestlers(true, [rosterId], 'last_name', 100, 'union');
      this.loading.roster_wrestlers = true;
      this.$apiService.loadAllPages(url, 'wrestlers')
        .then((wrestlers) => {
          // Guard against a stale response if the coach switched rosters mid load
          if (this.selectedRosterId !== rosterId) {
            return;
          }
          this.rosterWrestlers = wrestlers;
          this.loading.roster_wrestlers = false;
        })
        .catch((error) => {
          if (this.selectedRosterId !== rosterId) {
            return;
          }
          this.loading.roster_wrestlers = false;
          this.rosterErrorMessage = `Error retrieving roster wrestlers ${error.toString()}`;
          this.rosterError = true;
        });
    },
    createRosterPrintJob() {
      if (!this.canCreateRosterJob || this.loading.creating_roster) {
        return;
      }
      this.rosterError = false;
      this.loading.creating_roster = true;
      axios.post(this.$apiService.printJobsUrl(), this.rosterPrintJobParams)
        .then((response) => {
          this.$notificationManager.emit('show-toast', `Print job ${response.data.id} queued, refresh in a minute or two to download`, true);
          this.loading.creating_roster = false;
          this.loadPrintJobs();
        })
        .catch((error) => {
          this.loading.creating_roster = false;
          this.rosterErrorMessage = this.parseErrorResponse(error);
          this.rosterError = true;
        });
    },
    clearWrestlers() {
      const vm = this;
      this.wrestlers.forEach((wp) => { vm.$set(wp, 'checked', false); });
    },
    createPrintJob() {
      const url = `${this.$apiService.printJobsUrl()}`;
      this.loading.creating = true;
      axios.post(url, this.printJobParams)
        .then((response) => {
          this.$notificationManager.emit('show-toast', `Print job ${response.data.id} queued`, true);
          this.loading.creating = false;
          this.loadPrintJobs();
        })
        .catch((error) => {
          this.loading.creating = false;
          this.error = true;
          this.errorMessage = 'Error creating print job';
        });
    },
    loadPrintJobs() {
      this.loading.print_jobs = true;
      axios.get(this.$apiService.printJobsUrl())
        .then((response) => {
          this.printJobs = response.data.print_jobs;
          this.loading.print_jobs = false;
        }).catch((error) => {
          this.loading.print_jobs = false;
          this.error = true;
          this.errorMessage = 'Error loading print jobs';
        });
    },
    loadProfiles() {
      const url = this.$apiService.filteredWrestlers(this.showGuests, [], null, 100, 'union');
      this.loading.wrestlers = true;
      this.$apiService.loadAllPages(url, 'wrestlers')
        .then((objects) => {
          this.wrestlers = objects;
          this.loading.wrestlers = false;
        })
        .catch((error) => {
          this.loading.wrestlers = false;
          this.errorMessage = `Error retrieving wrestlers ${error.toString()}`;
          this.error = true;
        });
    },
  },
};
</script>
